import { createCrossSupabase, supabaseOptions } from "lib/supabaseClient";

export interface OneShotCompleteResult {
  content: string;
}

/** Server-driven phases during `funding_appeal_generate_template` (draft + optional review LLM passes). */
export type OneShotProgressPhase = "drafting" | "reviewing";

export interface OneShotCompleteOptions {
  /** When set, requests NDJSON progress from the chat function and fires as each phase starts. */
  onPhase?: (phase: OneShotProgressPhase) => void;
  /** When set (with stream-capable backend), draft token chunks stream in for live UI updates. */
  onDraftDelta?: (delta: string) => void;
}

/**
 * One-shot assistant completion via the chat edge function. No chat session is created.
 * The backend owns prompts and instructions—the frontend only sends a type and context.
 */
export async function oneShotComplete(
  type: string,
  context?: Record<string, unknown>,
  options?: OneShotCompleteOptions,
): Promise<OneShotCompleteResult> {
  const supabase = await createCrossSupabase();
  const baseUrl = supabaseOptions.url;
  const url = `${baseUrl}/functions/v1/chat`;
  const session = await supabase.auth.getSession();
  const accessToken = session.data.session?.access_token;

  if (!accessToken) {
    throw new Error("Not authenticated");
  }

  const streamProgress = options?.onPhase != null || options?.onDraftDelta != null;

  const res = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      oneShot: true,
      type,
      context,
      ...(streamProgress ? { streamProgress: true } : {}),
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? `Request failed: ${res.status}`);
  }

  const contentType = res.headers.get("content-type") ?? "";

  if (streamProgress && contentType.includes("ndjson")) {
    return parseNdjsonOneShot(res, options);
  }

  return res.json() as Promise<OneShotCompleteResult>;
}

function handleNdjsonLine(
  obj: Record<string, unknown>,
  options?: OneShotCompleteOptions,
): { content?: string } {
  if (typeof obj.error === "string") {
    throw new Error(obj.error);
  }
  if (obj.phase === "drafting" || obj.phase === "reviewing") {
    options?.onPhase?.(obj.phase);
  }
  if (typeof obj.draftDelta === "string") {
    options?.onDraftDelta?.(obj.draftDelta);
  }
  if (typeof obj.content === "string") {
    return { content: obj.content };
  }
  return {};
}

async function parseNdjsonOneShot(
  res: Response,
  options?: OneShotCompleteOptions,
): Promise<OneShotCompleteResult> {
  const reader = res.body?.getReader();
  if (!reader) {
    throw new Error("No response body");
  }
  const decoder = new TextDecoder();
  let buffer = "";
  let content = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      const obj = JSON.parse(trimmed) as Record<string, unknown>;
      const upd = handleNdjsonLine(obj, options);
      if (upd.content !== undefined) content = upd.content;
    }
  }
  const tail = buffer.trim();
  if (tail) {
    const obj = JSON.parse(tail) as Record<string, unknown>;
    const upd = handleNdjsonLine(obj, options);
    if (upd.content !== undefined) content = upd.content;
  }
  return { content };
}
